import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, KeyRound, Mail, Monitor, CheckCircle2, AlertTriangle,
  Loader2, Copy, QrCode, Lock, ChevronRight, MapPin, X, Smartphone, Download, Bell, Clock, Info,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useCockpit } from "./CockpitLayout";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};
const parseDevice = (ua) => {
  if (!ua) return "";
  const os = /Windows/i.test(ua) ? "Windows" : /Macintosh|Mac OS/i.test(ua) ? "macOS" : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Android/i.test(ua) ? "Android" : /Linux/i.test(ua) ? "Linux" : "";
  const br = /Edg/i.test(ua) ? "Edge" : /OPR|Opera/i.test(ua) ? "Opera" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "";
  if (br && os) return `${br} auf ${os}`;
  return br || os || "";
};

const inp = "w-full bg-black border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#A3E635] transition-colors";

/* ---------------- 2FA-Karte (Premium) ---------------- */
function TwoFACard({ accent, overview, reload }) {
  const { t } = useI18n();
  const { user, checkAuth } = useAuth();
  const enabled = !!(overview?.totp_enabled ?? user?.totp_enabled);
  const [mode, setMode] = useState("idle"); // idle | setup | disable
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const remaining = user?.backup_codes_remaining;
  const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fb; };
  const saveCodesFile = (codes) => {
    const blob = new Blob([`KEWAGPage — 2FA Backup-Codes\n(jeder Code ist nur EINMAL gueltig — sicher aufbewahren)\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "kewagpage-2fa-backup-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };
  const copyCodes = (codes) => { navigator.clipboard?.writeText(codes.join("\n")); toast.success(t("Backup-Codes kopiert")); };

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enable-start"); setQr(data.qr); setSecret(data.secret); setCode(""); setMode("setup"); }
    catch (e) { toast.error(errMsg(e, t("Einrichtung konnte nicht gestartet werden"))); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (code.trim().replace(/\s/g, "").length < 6) { toast.error(t("Bitte 6-stelligen Code aus Ihrer App eingeben")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable-confirm", { code: code.trim() });
      setBackupCodes(data.backup_codes || []);
      setQr(""); setSecret(""); setCode(""); setMode("backupcodes");
      toast.success(t("Zwei-Faktor-Login ist jetzt aktiv"));
    } catch (e) { toast.error(errMsg(e, t("Code ungültig"))); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!password) { toast.error(t("Bitte Ihr Passwort eingeben")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { password });
      setBackupCodes(data.backup_codes || []); setPassword(""); setMode("backupcodes");
      toast.success(t("Neue Backup-Codes erzeugt — die alten sind jetzt ungültig"));
    } catch (e) { toast.error(errMsg(e, t("Passwort falsch"))); }
    finally { setBusy(false); }
  };
  const finishCodes = async () => { setBackupCodes([]); setMode("idle"); await checkAuth(); reload(); };
  const disable = async () => {
    if (!password) { toast.error(t("Bitte Ihr Passwort eingeben")); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      toast.success(t("Zwei-Faktor-Login wurde deaktiviert"));
      setMode("idle"); setPassword(""); await checkAuth(); reload();
    } catch (e) { toast.error(errMsg(e, t("Passwort falsch"))); }
    finally { setBusy(false); }
  };
  const copySecret = () => { navigator.clipboard?.writeText(secret); toast.success(t("Schlüssel kopiert")); };

  return (
    <Card className="p-6 sm:p-7" data-testid="twofa-card">
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="shrink-0 mx-auto sm:mx-0">
          <div className="w-32 h-32 rounded-2xl flex items-center justify-center"
            style={{ background: `radial-gradient(circle at 50% 40%, ${accent}22, transparent 70%)` }}>
            <img src="/shield-2fa.png" alt={t("Sicherheit")} className="w-28 h-28 object-contain drop-shadow-[0_8px_24px_rgba(163,230,53,0.25)]" data-testid="twofa-shield" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-lg font-bold">{t("Zwei-Faktor-Login (2FA)")}</h2>
            {enabled ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border" data-testid="twofa-status-badge"
                style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }}><ShieldCheck className="w-3.5 h-3.5" /> {t("Aktiv")}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border" data-testid="twofa-status-badge"
                style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }}>{t("Empfohlen")}</span>
            )}
          </div>
          <p className="text-sm text-gray-400 mt-1.5">{t("Zusätzlicher Schutz über eine Authenticator-App (z. B. Google Authenticator, Authy).")}</p>

          {/* Status-Panel */}
          <div className="mt-4 grid grid-cols-3 gap-4 rounded-2xl border border-white/10 bg-black/30 p-4">
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500 mb-1.5">{t("Status")}</div>
              {enabled ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: `${accent}1A`, color: accent }} data-testid="twofa-panel-status">{t("Aktiv")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md bg-amber-400/15 text-amber-300" data-testid="twofa-panel-status">{t("Nicht aktiv")}</span>
              )}
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500 mb-1.5">{t("Schutzstufe")}</div>
              <div className="text-sm font-semibold text-gray-200">{enabled ? t("Erhöht") : t("Standard")}</div>
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500 mb-1.5">{t("Letzte Änderung")}</div>
              <div className="text-sm font-semibold text-gray-200">{enabled ? fmtDate(overview?.password_changed_at) : "—"}</div>
            </div>
          </div>

          {/* IDLE */}
          {mode === "idle" && (
            <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
              <p className="text-sm text-gray-400 flex-1 min-w-[220px]">
                {enabled ? t("Ihr Konto ist mit Zwei-Faktor-Login geschützt. Beim nächsten Login fragen wir zusätzlich einen Code aus Ihrer App ab.")
                  : t("2FA ist optional, wird aber empfohlen. Sie brauchen nur eine Authenticator-App auf Ihrem Handy.")}
              </p>
              {enabled ? (
                <div className="flex items-center gap-2.5 flex-wrap">
                  {typeof remaining === "number" && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${remaining <= 2 ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-white/10 bg-white/5 text-gray-300"}`} data-testid="twofa-backup-remaining">
                      {t("Backup-Codes übrig:")} {remaining}
                    </span>
                  )}
                  <button onClick={() => { setPassword(""); setMode("regenerate"); }} disabled={busy} data-testid="twofa-regen-btn"
                    className="border border-white/15 bg-white/5 hover:bg-white/10 text-gray-200 font-semibold py-2.5 px-4 rounded-xl text-sm disabled:opacity-60 transition-colors">
                    {t("Backup-Codes neu erzeugen")}
                  </button>
                  <button onClick={() => setMode("disable")} disabled={busy} data-testid="twofa-deactivate-btn"
                    className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold py-2.5 px-5 rounded-xl text-sm disabled:opacity-60 transition-colors">
                    {t("2FA deaktivieren")}
                  </button>
                </div>
              ) : (
                <button onClick={start} disabled={busy} data-testid="twofa-activate-btn"
                  className="font-bold py-2.5 px-5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90 transition-opacity"
                  style={{ background: accent, color: "#071005" }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("2FA aktivieren")}
                </button>
              )}
            </div>
          )}

          {/* SETUP */}
          {mode === "setup" && (
            <div className="mt-4 flex flex-col sm:flex-row gap-5 items-start" data-testid="twofa-setup-panel">
              <div className="bg-white p-3 rounded-xl shrink-0">
                {qr ? <img src={qr} alt={t("QR-Code für 2FA")} className="w-36 h-36" data-testid="twofa-qr" /> : <QrCode className="w-36 h-36 text-black" />}
              </div>
              <div className="flex-1 space-y-3 min-w-0">
                <p className="text-sm text-gray-300">{t("1. Öffnen Sie Ihre Authenticator-App und scannen Sie den QR-Code.")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-gray-300 break-all" data-testid="twofa-secret">{secret}</code>
                  <button onClick={copySecret} data-testid="twofa-copy-secret" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white p-2 rounded-lg shrink-0"><Copy className="w-4 h-4" /></button>
                </div>
                <p className="text-sm text-gray-300">{t("2. Geben Sie den 6-stelligen Code aus der App ein:")}</p>
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  inputMode="numeric" placeholder="000000" data-testid="twofa-code-input"
                  className="w-40 bg-black border border-white/10 rounded-lg px-3 py-2.5 text-lg tracking-[0.3em] font-mono text-center focus:outline-none" style={{ caretColor: accent }} />
                <div className="flex items-center gap-3 pt-1">
                  <button onClick={confirm} disabled={busy} data-testid="twofa-confirm-btn"
                    className="font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ background: accent, color: "#071005" }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Aktivieren & bestätigen")}
                  </button>
                  <button onClick={() => { setMode("idle"); setQr(""); setSecret(""); setCode(""); }} disabled={busy} data-testid="twofa-cancel-btn"
                    className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
                </div>
              </div>
            </div>
          )}

          {/* BACKUP-CODES (einmalige Anzeige) */}
          {mode === "backupcodes" && (
            <div className="mt-4 max-w-md" data-testid="twofa-backupcodes-panel">
              <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-4">
                <p className="text-sm text-amber-200 font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t("Ihre Backup-Codes — nur jetzt sichtbar")}</p>
                <p className="text-xs text-gray-400 mt-1">{t("Bewahren Sie diese sicher auf. Jeder Code funktioniert genau einmal, falls Sie keinen Zugriff auf Ihre Authenticator-App haben.")}</p>
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="twofa-backupcodes-list">
                  {backupCodes.map((c) => (
                    <code key={c} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm font-mono tracking-wider text-center text-gray-100">{c}</code>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button onClick={() => copyCodes(backupCodes)} data-testid="twofa-copy-codes" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><Copy className="w-4 h-4" /> {t("Kopieren")}</button>
                  <button onClick={() => saveCodesFile(backupCodes)} data-testid="twofa-download-codes" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><Download className="w-4 h-4" /> {t("Herunterladen")}</button>
                </div>
                <button onClick={finishCodes} data-testid="twofa-codes-saved-btn" className="mt-4 w-full font-bold py-2.5 px-5 rounded-xl text-sm hover:opacity-90" style={{ background: accent, color: "#071005" }}>{t("Ich habe die Codes sicher gespeichert")}</button>
              </div>
            </div>
          )}

          {/* BACKUP-CODES NEU ERZEUGEN (Passwort) */}
          {mode === "regenerate" && (
            <div className="mt-4 space-y-3 max-w-sm" data-testid="twofa-regen-panel">
              <p className="text-sm text-gray-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-300" /> {t("Passwort bestätigen, um neue Backup-Codes zu erzeugen. Die alten werden dabei ungültig.")}</p>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Ihr Passwort")} data-testid="twofa-regen-password" className={inp} />
              <div className="flex items-center gap-3">
                <button onClick={regenerate} disabled={busy} data-testid="twofa-regen-confirm-btn" className="font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ background: accent, color: "#071005" }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {t("Neue Codes erzeugen")}
                </button>
                <button onClick={() => { setMode("idle"); setPassword(""); }} disabled={busy} data-testid="twofa-regen-cancel-btn" className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
              </div>
            </div>
          )}

          {/* DISABLE */}
          {mode === "disable" && (
            <div className="mt-4 space-y-3 max-w-sm" data-testid="twofa-disable-panel">
              <p className="text-sm text-gray-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-300" /> {t("Bitte bestätigen Sie mit Ihrem Passwort, um 2FA zu deaktivieren.")}</p>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Ihr Passwort")} data-testid="twofa-disable-password" className={inp} />
              <div className="flex items-center gap-3">
                <button onClick={disable} disabled={busy} data-testid="twofa-disable-confirm-btn"
                  className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("2FA endgültig deaktivieren")}
                </button>
                <button onClick={() => { setMode("idle"); setPassword(""); }} disabled={busy} data-testid="twofa-disable-cancel-btn" className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ---------------- Sicherheitsübersicht (Score-Ring) ---------------- */
export function SecurityScore({ accent, overview }) {
  const { t } = useI18n();
  const failed = Number.isSafeInteger(overview?.failed_recent) && overview.failed_recent >= 0 ? overview.failed_recent : null;
  const twofa = typeof overview?.totp_enabled === "boolean" ? overview.totp_enabled : null;
  const hasPasswordDate = typeof overview?.password_changed_at === "string" && Number.isFinite(Date.parse(overview.password_changed_at));
  const color = failed > 0 ? "#FBBF24" : "#9CA3AF";
  const checks = [
    { ok: twofa === true, label: t(twofa === true ? "2FA aktiv" : twofa === false ? "2FA nicht aktiv" : "2FA-Status offen") },
    { ok: failed === 0, label: failed === null ? t("Anmeldedaten noch nicht verfügbar") : `${t("Erfasste fehlgeschlagene Anmeldungen (30 Tage)")}: ${failed}` },
    { ok: hasPasswordDate, label: hasPasswordDate ? `${t("Letzte Passwortänderung")}: ${fmtDate(overview.password_changed_at)}` : t("Letzte Passwortänderung nicht dokumentiert") },
    { ok: false, label: t("Zugriffsrechte separat prüfen") },
  ];
  return (
    <Card className="p-6" data-testid="security-score-card">
      <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-4 h-4" style={{ color: accent }} /><h3 className="font-bold">{t("Sicherheitsübersicht")}</h3></div>
      <div className="flex flex-col items-center text-center">
        <div className="w-[140px] h-[140px] rounded-full border-[10px] border-white/10 flex items-center justify-center" aria-hidden="true">
          <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${color}1A` }}><ShieldCheck className="w-6 h-6" style={{ color }} /></span>
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 mt-4">{t("Sicherheitsstatus")}</div>
        <div className="text-2xl font-black mt-1 break-words" style={{ color }} data-testid="security-score-status">{t(failed > 0 ? "Anmeldungen prüfen" : "Nachweise offen")}</div>
        <p className="text-sm text-gray-300 mt-3">{t("Prüfen Sie Ihre Anmeldungen und den Schutz Ihres Kontos.")}</p>
      </div>
      <ul className="mt-6 space-y-3">{checks.map((check, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm" data-testid={`security-check-${i}`}>
          {check.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent }} /> : <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />}
          <span className="text-gray-300 break-words min-w-0">{check.label}</span>
        </li>
      ))}</ul>
    </Card>
  );
}

/* ---------------- Sicherheitseinstellungen (Zeilen + Modale) ---------------- */
function SettingRow({ icon: Icon, accent, title, subtitle, btnLabel, onClick, testid }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex-wrap" data-testid={`${testid}-row`}>
      <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}18`, color: accent }}><Icon className="w-5 h-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[15px]">{title}</div>
        <div className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</div>
      </div>
      <button onClick={onClick} data-testid={testid}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-200 bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-xl transition-colors shrink-0">
        {btnLabel} <ChevronRight className="w-4 h-4 text-gray-500" />
      </button>
    </div>
  );
}

function SecuritySettings({ accent, overview, reload }) {
  const { t } = useI18n();
  const { clientId } = useCockpit();
  const { changePassword } = useAuth();
  const qs = clientId ? `?client_id=${clientId}` : "";
  const [modal, setModal] = useState(null); // pw | email | devices
  const [busy, setBusy] = useState(false);

  // Passwort
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const submitPw = async () => {
    if (nw.length < 10) { toast.error(t("Das Passwort muss mindestens 10 Zeichen lang sein.")); return; }
    if (nw !== cf) { toast.error(t("Die Passwörter stimmen nicht überein.")); return; }
    setBusy(true);
    try { await changePassword(cur, nw); toast.success(t("Passwort erfolgreich geändert.")); setModal(null); setCur(""); setNw(""); setCf(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Passwortwechsel fehlgeschlagen.")); }
    finally { setBusy(false); }
  };

  // E-Mail-Adresse (via KEWAG, aus Sicherheitsgründen)
  const [newEmail, setNewEmail] = useState(""); const [reason, setReason] = useState("");
  const submitEmail = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim())) { toast.error(t("Bitte eine gültige E-Mail-Adresse eingeben.")); return; }
    setBusy(true);
    try {
      await api.post(`/cockpit/support${qs}`, {
        subject: t("E-Mail-Adresse ändern"),
        category: "allgemein",
        message: `${t("Bitte ändern Sie meine Login-E-Mail-Adresse.")}\n\n${t("Aktuell")}: ${overview?.email || "—"}\n${t("Neu")}: ${newEmail.trim()}${reason.trim() ? `\n\n${t("Grund")}: ${reason.trim()}` : ""}`,
      });
      toast.success(t("Anfrage an KEWAG gesendet — wir melden uns bei Ihnen."));
      setModal(null); setNewEmail(""); setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || t("Anfrage konnte nicht gesendet werden.")); }
    finally { setBusy(false); }
  };

  const logins = overview?.last_logins || [];

  return (
    <Card className="p-6" data-testid="security-settings-card">
      <h3 className="font-bold mb-4">{t("Sicherheitseinstellungen")}</h3>
      <div className="space-y-3">
        <SettingRow icon={KeyRound} accent={accent} title={t("Passwort")}
          subtitle={`${t("Letzte Änderung")}: ${fmtDate(overview?.password_changed_at)}`}
          btnLabel={t("Passwort ändern")} onClick={() => setModal("pw")} testid="pw-change-open" />
        <SettingRow icon={Mail} accent={accent} title={t("E-Mail-Adresse")}
          subtitle={overview?.email || "—"}
          btnLabel={t("E-Mail-Adresse ändern")} onClick={() => setModal("email")} testid="email-change-open" />
        <SettingRow icon={Monitor} accent={accent} title={t("Geräte & Sitzungen")}
          subtitle={t("Verwalten Sie Geräte, auf denen Sie aktuell angemeldet sind.")}
          btnLabel={t("Geräte verwalten")} onClick={() => setModal("devices")} testid="devices-open" />
      </div>

      {/* Passwort-Modal */}
      <Dialog open={modal === "pw"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#0E0F12] border-white/10 text-white" data-testid="pw-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" style={{ color: accent }} /> {t("Passwort ändern")}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">{t("Wählen Sie ein neues, sicheres Passwort für Ihren Zugang.")}</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-gray-300">{t("Mindestens 10 Zeichen mit Gross- und Kleinbuchstaben, Ziffer und Sonderzeichen.")}</p>
            <div><label className="text-xs text-gray-400 mb-1 block">{t("Aktuelles Passwort")}</label>
              <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={inp} data-testid="pw-current" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">{t("Neues Passwort")}</label>
              <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className={inp} data-testid="pw-new" placeholder={t("Mindestens 10 Zeichen")} /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">{t("Neues Passwort bestätigen")}</label>
              <input type="password" value={cf} onChange={(e) => setCf(e.target.value)} className={inp} data-testid="pw-confirm" /></div>
            <button onClick={submitPw} disabled={busy} data-testid="pw-submit"
              className="w-full font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 hover:opacity-90 mt-1" style={{ background: accent, color: "#071005" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Passwort speichern")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* E-Mail-Modal */}
      <Dialog open={modal === "email"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#0E0F12] border-white/10 text-white" data-testid="email-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" style={{ color: accent }} /> {t("E-Mail-Adresse ändern")}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">{t("Ihre Login-E-Mail-Adresse wird aus Sicherheitsgründen durch das KEWAG-Team geändert.")}</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-gray-400">{t("Aus Sicherheitsgründen wird Ihre Login-E-Mail-Adresse durch das KEWAG-Team geändert. Bitte geben Sie Ihre gewünschte neue Adresse an.")}</p>
            <div className="text-xs text-gray-500 rounded-lg bg-white/5 border border-white/10 px-3 py-2">{t("Aktuell")}: <span className="text-gray-300">{overview?.email || "—"}</span></div>
            <div><label className="text-xs text-gray-400 mb-1 block">{t("Neue E-Mail-Adresse")}</label>
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inp} data-testid="email-new" placeholder="name@firma.ch" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">{t("Grund (optional)")}</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={inp} data-testid="email-reason" /></div>
            <button onClick={submitEmail} disabled={busy} data-testid="email-submit"
              className="w-full font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 hover:opacity-90 mt-1" style={{ background: accent, color: "#071005" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t("Änderung anfragen")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Geräte-Modal */}
      <Dialog open={modal === "devices"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#0E0F12] border-white/10 text-white" data-testid="devices-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Monitor className="w-5 h-5" style={{ color: accent }} /> {t("Geräte & Sitzungen")}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">{t("Übersicht Ihrer zuletzt angemeldeten Geräte.")}</DialogDescription></DialogHeader>
          <div className="pt-1">
            {logins.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">{t("Noch keine Anmeldungen erfasst.")}</div>
            ) : (
              <ul className="divide-y divide-white/5 max-h-[50vh] overflow-y-auto -mr-2 pr-2">
                {logins.map((l, i) => {
                  const dev = parseDevice(l.user_agent);
                  return (
                    <li key={i} className="py-3 flex items-center gap-3" data-testid={`device-row-${i}`}>
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-white/5 text-gray-300"><Monitor className="w-4 h-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                          {dev || t("Gerät unbekannt")}
                          {i === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${accent}1A`, color: accent }}>{t("Letzte erfasste Anmeldung")}</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{l.location || l.ip || "—"} · {fmtDateTime(l.created_at)}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-[11px] text-gray-600 mt-3 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} />
              {t("Kommt Ihnen eine Anmeldung nicht bekannt vor? Ändern Sie Ihr Passwort und melden Sie sich bei KEWAG.")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Letzte Anmeldungen ---------------- */
function RecentLogins({ accent, overview, loadError }) {
  const { t } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const logins = overview?.last_logins || [];
  const shown = showAll ? logins : logins.slice(0, 3);

  return (
    <Card className="p-6" data-testid="recent-logins-card">
      <h3 className="font-bold mb-4">{t("Letzte Anmeldungen")}</h3>
      {loadError ? <p className="text-sm text-amber-200">{t("Anmeldedaten noch nicht verfügbar")}</p> : overview == null ? (
        <div className="text-sm text-gray-500 py-4">{t("Lade …")}</div>
      ) : logins.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-8" data-testid="logins-empty">
          <span className="w-11 h-11 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10 mb-3"><Monitor className="w-5 h-5 text-gray-500" /></span>
          <div className="text-sm font-semibold text-gray-300">{t("Noch keine Anmeldungen erfasst.")}</div>
          <p className="text-xs text-gray-500 mt-1">{t("Ihre Anmeldungen erscheinen hier, sobald Sie sich einloggen.")}</p>
        </div>
      ) : (
        <>
          <ul className="space-y-1">
            {shown.map((l, i) => {
              const dev = parseDevice(l.user_agent);
              const loc = l.location || l.ip;
              return (
                <li key={i} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`login-row-${i}`}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accent}18`, color: accent }}>
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold flex items-center gap-2 flex-wrap">
                      {fmtDateTime(l.created_at)}
                      {i === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${accent}1A`, color: accent }}>{t("Letzte erfasste Anmeldung")}</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                      {loc && <MapPin className="w-3 h-3 text-gray-600 shrink-0" />}
                      <span className="truncate">{[loc, dev].filter(Boolean).join(" • ") || t("Gerät unbekannt")}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {logins.length > 3 && (
            <button onClick={() => setShowAll((v) => !v)} data-testid="logins-show-all"
              className="mt-4 text-sm font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity" style={{ color: accent }}>
              {showAll ? t("Weniger anzeigen") : t("Alle Aktivitäten ansehen")} <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </>
      )}
    </Card>
  );
}

/* ---------------- PWA / App nutzen (ehrlicher Status) ---------------- */
function PwaAppCard({ accent }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const platform = /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "desktop";

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const install = async () => {
    if (!installEvt) { setOpen(true); return; }
    installEvt.prompt();
    try { await installEvt.userChoice; } catch {}
    setInstallEvt(null);
  };

  const steps = {
    ios: [
      t("Tippen Sie unten in Safari auf das Teilen-Symbol."),
      t("Wählen Sie «Zum Home-Bildschirm»."),
      t("Bestätigen Sie mit «Hinzufügen» und öffnen Sie KEWAGpage künftig über das App-Symbol."),
    ],
    android: [
      t("Tippen Sie oben rechts im Browser auf das Menü (drei Punkte)."),
      t("Wählen Sie «App installieren» bzw. «Zum Startbildschirm hinzufügen»."),
      t("Bestätigen Sie — KEWAGpage erscheint als App-Symbol."),
    ],
    desktop: [
      t("Klicken Sie in der Adressleiste auf das Installations-Symbol."),
      t("Wählen Sie «Installieren»."),
      t("Alternativ über das Browser-Menü «KEWAGpage installieren»."),
    ],
  };

  const StatusRow = ({ pending, label }) => {
    const color = pending ? "#FBBF24" : accent;
    const Ic = pending ? Clock : CheckCircle2;
    return (
      <li className="flex items-center gap-2.5 text-sm">
        <Ic className="w-4 h-4 shrink-0" style={{ color }} />
        <span className="flex-1 text-gray-300">{label}</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}1A`, color }}>{pending ? t("Vorbereitet") : t("Aktiv")}</span>
      </li>
    );
  };

  return (
    <Card className="p-6 sm:p-7" data-testid="pwa-app-card">
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${accent}18`, color: accent }}>
          <Smartphone className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-lg font-bold">{t("KEWAGpage als App nutzen")}</h2>
            {isStandalone
              ? <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border" data-testid="pwa-status-badge" style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }}><CheckCircle2 className="w-3.5 h-3.5" /> {t("Als App installiert")}</span>
              : <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300" data-testid="pwa-status-badge"><Info className="w-3.5 h-3.5" /> {t("Empfohlen")}</span>}
          </div>
          <p className="text-sm text-gray-400 mt-1.5">
            {t("Fügen Sie KEWAGpage zu Ihrem Home-Bildschirm hinzu, damit Sie das Kunden-Cockpit wie eine App öffnen und später Push-Benachrichtigungen erhalten können.")}
          </p>
        </div>
      </div>

      <ul className="mt-5 space-y-2.5 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="pwa-status-list">
        <StatusRow pending={false} label={t("In-App-Benachrichtigungen & Glocke")} />
        <StatusRow pending={!isStandalone} label={isStandalone ? t("App zum Home-Bildschirm hinzugefügt") : t("App zum Home-Bildschirm hinzufügen")} />
        <StatusRow pending={true} label={t("Push-Benachrichtigungen — folgen als nächster Schritt")} />
      </ul>

      <p className="text-[11px] text-gray-500 mt-3 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} />
        {t("Push-Benachrichtigungen sind vorbereitet, aber noch nicht aktiviert. Am Handy funktionieren sie erst, wenn KEWAGpage zum Home-Bildschirm hinzugefügt wurde (auf dem iPhone Pflicht).")}
      </p>

      {!isStandalone && (
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {installEvt && (
            <button onClick={install} data-testid="pwa-install-btn"
              className="font-bold py-2.5 px-5 rounded-xl text-sm flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background: accent, color: "#071005" }}>
              <Download className="w-4 h-4" /> {t("App installieren")}
            </button>
          )}
          <button onClick={() => setOpen(true)} data-testid="pwa-guide-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-200 bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 px-5 rounded-xl transition-colors">
            {t("Anleitung anzeigen")} <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0E0F12] border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="pwa-guide-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5" style={{ color: accent }} /> {t("KEWAGpage als App hinzufügen")}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">{t("So installieren Sie das Kunden-Cockpit auf Ihrem Gerät.")}</DialogDescription>
          </DialogHeader>
          <div className="pt-1 space-y-4">
            {[["ios", t("iPhone / iPad (Safari)")], ["android", t("Android (Chrome)")], ["desktop", t("Computer (Chrome / Edge)")]].map(([key, label]) => (
              <div key={key} className={`rounded-xl border p-4 ${platform === key ? "bg-white/[0.04]" : "border-white/10"}`} style={platform === key ? { borderColor: `${accent}66` } : {}} data-testid={`pwa-steps-${key}`}>
                <div className="text-sm font-bold mb-2 flex items-center gap-2">
                  {label}
                  {platform === key && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${accent}1A`, color: accent }}>{t("Ihr Gerät")}</span>}
                </div>
                <ol className="space-y-1.5">
                  {steps[key].map((s, i) => (
                    <li key={i} className="text-sm text-gray-300 flex gap-2.5">
                      <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: `${accent}1A`, color: accent }}>{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
            <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
              <Bell className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} />
              {t("Sobald Push-Benachrichtigungen aktiviert sind, informieren wir Sie hier. Bis dahin erhalten Sie alle Hinweise in der Glocke oben und — sobald freigeschaltet — per E-Mail.")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Seite ---------------- */
export default function CSecurity() {
  const { accent } = useCockpit();
  const { t } = useI18n();
  const [overview, setOverview] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const requestVersion = useRef(0);
  const reload = useCallback(async () => {
    const version = ++requestVersion.current;
    setOverview(null); setLoadError(false);
    try {
      const { data } = await api.get("/auth/security-overview");
      if (!data || typeof data !== "object" || Array.isArray(data)) throw new Error("Invalid security overview");
      if (version === requestVersion.current) setOverview(data);
    } catch {
      if (version === requestVersion.current) setLoadError(true);
    }
  }, []);
  useEffect(() => { reload(); return () => { requestVersion.current++; }; }, [reload]);
  const ov = overview || null;

  return (
    <div className="fade-in space-y-5" data-testid="cockpit-security">
      <div>
        <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><ShieldCheck className="w-4 h-4" /> {t("Sicherheit")}</p>
        <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Konto & Sicherheit")}</h1>
        <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Schützen Sie Ihren Zugang zum Kunden-Cockpit.")}</p>
      </div>

      {loadError && <div role="alert" data-testid="security-load-error" className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-200">
        <p>{t("Kontodaten konnten nicht geladen werden. Der Sicherheitsstand ist offen.")}</p>
        <button onClick={reload} className="min-h-[44px] underline">{t("Erneut versuchen")}</button>
      </div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5">
          <TwoFACard accent={accent} overview={ov} reload={reload} />
          <SecuritySettings accent={accent} overview={ov} reload={reload} />
          <PwaAppCard accent={accent} />
        </div>
        <div className="space-y-5">
          <SecurityScore accent={accent} overview={ov} />
          <RecentLogins accent={accent} overview={ov} loadError={loadError} />
        </div>
      </div>
    </div>
  );
}
